import React, { useState, useEffect, useCallback } from 'react';

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '642040136241';
const GCAL_ENDPOINT = process.env.REACT_APP_GCAL_ENDPOINT;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 6); // 6–17

function formatHour(h) {
  if (h === 0) return '12am';
  if (h < 12) return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}

function formatDateFull(date) {
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = MONTHS;
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const SERVICES = [
  { id: 'clean', label: 'Home clean' },
  { id: 'pets', label: 'Pet care' },
  { id: 'errands', label: 'Errands' },
  { id: 'childcare', label: 'Childcare' },
  { id: 'pa', label: 'Weekly PA $200/wk', amber: true },
  { id: 'other', label: 'Other' },
];

const PET_PILLS = ['Walks','Home visits','Vet appointments','Groomer drop-off','Feeding & check-ins','Something else'];

function ServiceNote({ serviceId, petPill, setPetPill }) {
  if (serviceId === 'pa') {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #FFF5E8, #FDECD4)',
        border: '0.5px solid #E8C8A0',
        borderRadius: '0.85rem',
        padding: '1rem',
        fontSize: '0.83rem',
        color: '#8A4A1A',
        lineHeight: 1.6,
        marginTop: '0.75rem',
      }}>
        <strong>Booking for Weekly PA?</strong> Your selected start time marks the beginning of your first session. The weekly arrangement runs from that point each week — just confirm the day and time that suits you best.
      </div>
    );
  }
  if (serviceId === 'childcare') {
    return (
      <div style={{
        background: '#F5F2FF',
        border: '0.5px solid #D4CAF0',
        borderRadius: '0.85rem',
        padding: '1rem',
        fontSize: '0.83rem',
        color: '#4A3A8A',
        lineHeight: 1.6,
        marginTop: '0.75rem',
      }}>
        Looking for someone to look after the kids? Whether it's a night out, an event, or anything in between — just get in touch and we'll sort it out together.
      </div>
    );
  }
  if (serviceId === 'pets') {
    return (
      <div style={{
        background: '#F2F8F4',
        border: '0.5px solid #A8C8B0',
        borderRadius: '0.85rem',
        padding: '1rem',
        marginTop: '0.75rem',
      }}>
        <div style={{ fontSize: '0.83rem', color: '#3A5A48', fontWeight: 500, marginBottom: '0.6rem' }}>
          What kind of pet care do you need?
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
          {PET_PILLS.map((pill) => (
            <button
              key={pill}
              onClick={() => setPetPill(petPill === pill ? null : pill)}
              style={{
                background: petPill === pill ? '#3A5A48' : '#fff',
                color: petPill === pill ? '#fff' : '#3A5A48',
                border: '0.5px solid #A8C8B0',
                borderRadius: '2rem',
                padding: '0.3rem 0.75rem',
                fontSize: '0.78rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {pill}
            </button>
          ))}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6A8A78' }}>
          Let me know in the WhatsApp message what you need and I'll confirm availability.
        </div>
      </div>
    );
  }
  return null;
}

export default function CalendarBooking() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [petPill, setPetPill] = useState(null);
  const [busySlots, setBusySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchAvailability = useCallback(async (dateStr) => {
    if (!GCAL_ENDPOINT) {
      setBusySlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const res = await fetch(`${GCAL_ENDPOINT}?date=${dateStr}`);
      const data = await res.json();
      setBusySlots(data.busy || []);
    } catch {
      setBusySlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(toISO(selectedDate));
    }
  }, [selectedDate, fetchAvailability]);

  // Calendar grid data
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(v => v - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(v => v + 1); }
    else setViewMonth(m => m + 1);
  }

  function handleDateClick(day) {
    const d = new Date(viewYear, viewMonth, day);
    if (d < today) return;
    setSelectedDate(d);
    setSelectedHour(null);
    setSelectedService(null);
    setPetPill(null);
  }

  function isToday(day) {
    const d = new Date(viewYear, viewMonth, day);
    return d.getTime() === today.getTime();
  }
  function isPast(day) {
    const d = new Date(viewYear, viewMonth, day);
    return d < today;
  }
  function isSelected(day) {
    if (!selectedDate) return false;
    const d = new Date(viewYear, viewMonth, day);
    return d.getTime() === selectedDate.getTime();
  }
  function isBusyHour(h) {
    const padded = String(h).padStart(2, '0') + ':00';
    return busySlots.includes(padded);
  }

  // Build WhatsApp message
  function buildWAMessage() {
    if (!selectedDate || selectedHour === null || !selectedService) return '';
    const svc = SERVICES.find(s => s.id === selectedService);
    const svcLabel = petPill && selectedService === 'pets'
      ? `Pet care – ${petPill}`
      : svc?.label;
    let msg = `Hi! I'd like to request a booking.\nDate: ${formatDateFull(selectedDate)}\nTime: ${formatHour(selectedHour)}\nService: ${svcLabel}`;
    if (selectedService === 'pa') {
      msg += `\nNote: I understand this time slot will be my recurring weekly start time.`;
    }
    msg += `\n\nName:\nContact:`;
    return msg;
  }

  const waMsg = buildWAMessage();
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMsg)}`;
  const canBook = selectedDate !== null && selectedHour !== null && selectedService !== null;

  const hasBusySlots = busySlots.length > 0;

  return (
    <section id="availability" style={{ background: '#fff', padding: '3.5rem 0' }}>
      <div className="content-column">
        <div className="section-tag">Availability</div>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 600,
          fontSize: '1.75rem',
          lineHeight: 1.2,
          color: '#1A1530',
          marginTop: 0,
          marginBottom: '0.5rem',
        }}>
          Pick a <em style={{ fontWeight: 400, color: '#7B6EC4' }}>time</em>
        </h2>
        <p style={{
          fontSize: '0.9rem',
          fontWeight: 300,
          color: '#6A6080',
          marginTop: 0,
          marginBottom: '1.25rem',
          lineHeight: 1.6,
        }}>
          Select a date then choose your preferred time slot — 6am to 6pm, any day.
        </p>

        {/* Amber note */}
        <div style={{
          background: 'linear-gradient(135deg, #FFF5E8, #FDECD4)',
          border: '0.5px solid #E8C8A0',
          borderRadius: '0.85rem',
          padding: '0.85rem 1rem',
          fontSize: '0.83rem',
          color: '#8A4A1A',
          lineHeight: 1.6,
          marginBottom: '1.25rem',
        }}>
          If a time looks unavailable, reach out anyway — there's a good chance we can figure something out.
        </div>

        {/* Calendar card */}
        <div className="card" style={{ marginBottom: '1.25rem', padding: '1.25rem' }}>
          {/* Month nav */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
          }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#6A6080' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 5L7 10L12 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: '1.05rem', color: '#1A1530' }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#6A6080' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: '0.4rem' }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 500, color: '#6A6080', padding: '0.2rem 0' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {/* Empty cells for offset */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const past = isPast(day);
              const sel = isSelected(day);
              const tod = isToday(day);

              let bg = 'transparent';
              let color = '#1A1530';
              let border = 'none';
              let cursor = 'pointer';

              if (sel) {
                bg = '#7B6EC4';
                color = '#fff';
              } else if (past) {
                color = '#C0B8D0';
                cursor = 'default';
              } else if (tod) {
                border = '1.5px solid #7B6EC4';
                bg = 'transparent';
              } else {
                bg = '#F5F2FF';
              }

              return (
                <button
                  key={day}
                  onClick={() => !past && handleDateClick(day)}
                  disabled={past}
                  style={{
                    background: bg,
                    color,
                    border,
                    borderRadius: '0.5rem',
                    padding: '0.45rem 0',
                    fontSize: '0.83rem',
                    fontWeight: sel ? 600 : 400,
                    cursor,
                    transition: 'all 0.12s',
                    outline: 'none',
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
            {[
              { color: '#F5F2FF', label: 'Available' },
              { color: '#7B6EC4', label: 'Selected' },
              { color: '#C0B8D0', label: 'Past' },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: color, border: '0.5px solid #E8E0F0' }} />
                <span style={{ fontSize: '0.72rem', color: '#6A6080' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Time slot picker */}
        {selectedDate && (
          <div className="card" style={{ marginBottom: '1.25rem', padding: '1.25rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1A1530', marginBottom: '0.75rem' }}>
              {formatDateFull(selectedDate)} — choose a time
            </div>

            {loadingSlots ? (
              <div style={{ color: '#6A6080', fontSize: '0.85rem' }}>Checking availability…</div>
            ) : (
              <>
                {hasBusySlots && (
                  <div style={{
                    background: '#F5F2FF',
                    borderRadius: '0.6rem',
                    padding: '0.6rem 0.85rem',
                    fontSize: '0.78rem',
                    color: '#6A6080',
                    marginBottom: '0.75rem',
                    lineHeight: 1.5,
                  }}>
                    Some slots are blocked this day — but reach out anyway, we can likely sort something.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  {HOURS.map((h) => {
                    const busy = isBusyHour(h);
                    const sel = selectedHour === h;
                    return (
                      <button
                        key={h}
                        onClick={() => !busy && setSelectedHour(h)}
                        disabled={busy}
                        style={{
                          background: sel ? '#7B6EC4' : busy ? '#F0EDF8' : '#F5F2FF',
                          color: sel ? '#fff' : busy ? '#C0B8D0' : '#1A1530',
                          border: '0.5px solid #E8E0F0',
                          borderRadius: '0.6rem',
                          padding: '0.55rem 0.3rem',
                          fontSize: '0.83rem',
                          fontWeight: sel ? 600 : 400,
                          cursor: busy ? 'not-allowed' : 'pointer',
                          textDecoration: busy ? 'line-through' : 'none',
                          transition: 'all 0.12s',
                        }}
                      >
                        {formatHour(h)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Service picker */}
        {selectedDate && selectedHour !== null && (
          <div className="card" style={{ marginBottom: '1.25rem', padding: '1.25rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1A1530', marginBottom: '0.75rem' }}>
              What service do you need?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {SERVICES.map((svc) => {
                const sel = selectedService === svc.id;
                return (
                  <button
                    key={svc.id}
                    onClick={() => { setSelectedService(svc.id); setPetPill(null); }}
                    style={{
                      background: sel
                        ? (svc.amber ? '#C17A3A' : '#7B6EC4')
                        : (svc.amber ? '#FFF5E8' : '#F5F2FF'),
                      color: sel ? '#fff' : (svc.amber ? '#8A4A1A' : '#1A1530'),
                      border: `0.5px solid ${svc.amber ? '#E8C8A0' : '#E8E0F0'}`,
                      borderRadius: '0.7rem',
                      padding: '0.65rem 0.5rem',
                      fontSize: '0.83rem',
                      fontWeight: sel ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.12s',
                      textAlign: 'center',
                    }}
                  >
                    {svc.label}
                  </button>
                );
              })}
            </div>
            {selectedService && (
              <ServiceNote serviceId={selectedService} petPill={petPill} setPetPill={setPetPill} />
            )}
          </div>
        )}

        {/* Summary + WhatsApp button */}
        <div style={{
          background: canBook ? 'linear-gradient(135deg, #EDE8FB, #F5F2FF)' : '#F8F8F8',
          border: '0.5px solid #E8E0F0',
          borderRadius: '1.25rem',
          padding: '1.25rem',
        }}>
          {canBook ? (
            <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: '#1A1530', lineHeight: 1.6 }}>
              <strong style={{ display: 'block', marginBottom: '0.3rem' }}>Your booking request:</strong>
              <span style={{ color: '#7B6EC4' }}>{formatDateFull(selectedDate)}</span>
              {' · '}
              <span style={{ color: '#7B6EC4' }}>{formatHour(selectedHour)}</span>
              {' · '}
              <span style={{ color: '#7B6EC4' }}>
                {SERVICES.find(s => s.id === selectedService)?.label}
                {selectedService === 'pets' && petPill ? ` – ${petPill}` : ''}
              </span>
            </div>
          ) : (
            <div style={{ marginBottom: '1rem', fontSize: '0.83rem', color: '#6A6080' }}>
              Select a date, time, and service to continue.
            </div>
          )}

          <a
            href={canBook ? waLink : undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp"
            style={{
              width: '100%',
              justifyContent: 'center',
              pointerEvents: canBook ? 'auto' : 'none',
              opacity: canBook ? 1 : 0.45,
            }}
          >
            {/* WhatsApp icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Send booking via WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
